import { useState, useEffect, useRef } from "react";
import "survey-core/defaultV2.min.css";
import { Model } from "survey-core";
import { Survey } from "survey-react-ui";
import customTheme from "./survey_theme.json";
import {
  useCreateIncidentMutation,
  useGetIncidentCreationDetailsQuery,
} from "../../../features/Incidents/IncidentSlice";
import { Loader } from "../../common/Loader/Loader";
import toast from "react-hot-toast";
import useRegexMatch from "../../../hooks/useRegexMatch";
import { mergeObjectsFromArray } from "../../../utils/commonMethods";
import { useLocation, useNavigate } from "react-router";
import { baseUrl } from "../../../utils/commonMethods";
import { setCredentials, logOut } from "../../../features/auth/authSlice";
import { useDispatch } from "react-redux";

function IncidentForm({
  defaultValues,
  selectedTypes,
  showConformationModal,
  toggleModal,
}) {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const incidentRelation = queryParams.get("incidentRelation");
  const relatedIncidentId = queryParams.get("relatedIncidentId");
  const linkedIncidentId = localStorage.getItem("fromIncidentId") || null;
  const [currentOrder, setCurrentOrder] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [incidentCreationData, setIncidentCreationData] = useState([]);
  const [sortedData, setSortedData] = useState([]);

  const submittedFormDataRef = useRef([]);
  const updateSumittedFormDataRef = (newValue) => {
    submittedFormDataRef.current = [...submittedFormDataRef.current, newValue];
  };

  useRegexMatch();

  const {
    data: incidentCreation,
    isLoading,
    isSuccess,
  } = useGetIncidentCreationDetailsQuery(selectedTypes);

  const [createIncident, { isLoading: createIncidentIsLoading }] =
    useCreateIncidentMutation();

  useEffect(() => {
    if (incidentCreation) {
      const data = incidentCreation.ids.map((id) => {
        return incidentCreation.entities[id];
      });
      const [incidentForms] = data;
      if (incidentForms?.forms.length > 0) {
        setSortedData(
          incidentForms?.forms?.slice().sort((a, b) => a.order - b.order)
        );
        setIncidentCreationData(incidentForms);
      }
    }
  }, [incidentCreation]);

  useEffect(() => {
    if (currentOrder >= incidentCreationData?.forms?.length) {
      handleSave();
    }
  }, [currentOrder]);

  const handleSave = async () => {
    try {
      const values = {
        name: "",
        story: "",
        details: "",
        typeId: selectedTypes?.typeId,
        subTypeId: selectedTypes?.subTypeId,
        formsData: answers,
      };

      if (incidentRelation === "linked") {
        values.linkedIncident = {
          fromIncidentId: linkedIncidentId,
          linkTypeId: 1,
        };
      }

      if (incidentRelation === "related") {
        values.linkedIncident = {
          fromIncidentId: relatedIncidentId,
          linkTypeId: 2,
        };
      }

      const response = await createIncident(values).unwrap();
      if (location.search === "") {
        localStorage.setItem("fromIncidentId", response?.guid);
      }

      if (incidentRelation === "related") {
        toggleModal();
        showConformationModal(false);
        navigate(`/incidents/view/${response?.guid}`);
      } else {
        showConformationModal(true);
      }

      toast.success(
        `${
          incidentRelation === "related"
            ? "Related"
            : incidentRelation === "linked"
            ? "Linked"
            : "New"
        } Incident Created Successfully`
      );

      updateSumittedFormDataRef([]);
    } catch (error) {
      toast.error(error?.data?.Message);
    }
  };

  if (isLoading || createIncidentIsLoading) {
    return <Loader />;
  }

  if (isSuccess && !isLoading) {
    const form = sortedData[currentOrder];
    const formTemplate = form?.structure;

    const survey = new Model(formTemplate);
    survey.applyTheme(customTheme);

    if (survey.title === "Incident Detail") {
      survey.pages[0].visible = false;
    }

    if (survey.title === "Persons Details") {
      if (defaultValues?.IncidentGroup === 1) {
        const question = survey.getQuestionByName("person_type");
        question.value = "Not applicable";
      }
    }

    survey.mergeData({
      ...defaultValues,
      // Incident_ExtRegServIndicator: incidentExtRegServIndicator.current,
      // Incident_CostCentre: incidentCostCentre.current,
      ...mergeObjectsFromArray(submittedFormDataRef.current),
    });

    if (!form) {
      return <></>;
    }

    survey.focusFirstQuestion();

    survey.onValueChanged.add(async function (sender, options) {
      if (options.name === "Incident_Date") {
        const subscribedQuestion = survey.getQuestionByName(
          "Incident_CSTReportDate"
        );
        subscribedQuestion.value = options.value;
      }

      if (options.name === "Incident_CostCentre") {
        const costCenterKey = options.value; // use the new value directly
        if (!costCenterKey) return;
        const url = `${baseUrl}/hierarchy/cost-centers/${costCenterKey}`;
        sendRequest(
          url,
          dispatch,
          (data) => {
            const questions = [
              { name: "Incident_L1_Directorate", data: data?.directorate },
              {
                name: "Incident_L2_ReportingStream",
                data: data?.reportingStream,
              },
              { name: "Incident_L3_Department", data: data?.department },
              { name: "Incident_L4_Sub-department", data: data?.subDepartment },
              { name: "Incident_L5_Area", data: data?.area },
            ];

            questions.forEach(({ name, data }) => {
              const q = sender.getQuestionByName(name);
              if (!q) return;

              if (data) {
                q.choices = [
                  {
                    value: data.key,
                    text: `${data.key} - ${data.description}`,
                  },
                ];
                sender.setValue(name, data.key);
              } else {
                q.choices = [];
                sender.setValue(name, null);
              }
            });
          },
          (status, response) => {
            console.error(
              "Failed to load cost center details:",
              status,
              response
            );
          }
        );
      }
    });

    survey.onAfterRenderSurvey.add(function (sender, options) {
      const hasVisibleQuestions = survey.pages.some((page) =>
        page.questions.some((question) => question.isVisible)
      );

      if (!hasVisibleQuestions) {
        const result = {
          incidentFormId: form.incidentFormId,
          data: {},
        };
        setAnswers((prev) => [...prev, result]);
        setCurrentOrder((prev) => prev + 1);
      }
    });

    survey.onComplete.add(function (sender, options) {
      const prevFormData = {
        ...mergeObjectsFromArray(answers.map((answer) => answer.data)),
      };

      const currentFormData =
        form.incidentFormId === 31 || form.incidentFormId === 15
          ? {
              ...sender.data,
              ...defaultValues,
            }
          : sender.data;
      updateSumittedFormDataRef(currentFormData);
      const finalFormData =
        answers.length > 0
          ? Object.keys(currentFormData)
              .filter((key) => !prevFormData.hasOwnProperty(key))
              .reduce((res, key) => {
                res[key] = currentFormData[key];
                return res;
              }, {})
          : null;

      const result = {
        incidentFormId: form.incidentFormId,
        data: finalFormData ?? currentFormData,
      };
      setAnswers((prev) => [...prev, result]);
      setCurrentOrder((prev) => prev + 1);
    });

    survey.onChoicesLazyLoad.add((_, options) => {
      if (
        options.question.getType() === "dropdown" &&
        options.question.name === "Incident_L5_Senior_Incident_Manager"
      ) {
        const page = options.skip / options.take + 1;
        const url = `${baseUrl}/users/?page=${page}&pageSize=${options.take}&name=${options.filter}`;

        sendRequest(url, dispatch, (data) => {
          options.setItems(
            data.items.map((x) => ({
              value: x.id,
              text: `${x.firstName} ${x.lastName} (${x.email})`,
            })),
            data.total
          );
        });
      }
      if (
        options.question.getType() === "dropdown" &&
        options.question.name === "Incident_L5_Local_Incident_Manager"
      ) {
        const page = options.skip / options.take + 1;
        const url = `${baseUrl}/users/?page=${page}&pageSize=${options.take}&name=${options.filter}`;

        sendRequest(url, dispatch, (data) => {
          options.setItems(
            data.items.map((x) => ({
              value: x.id,
              text: `${x.firstName} ${x.lastName} (${x.email}) `,
            })),
            data.total
          );
        });
      }
      if (
        options.question.getType() === "dropdown" &&
        options.question.name === "Incident_CostCentre"
      ) {
        const page = options.skip / options.take + 1;
        const url = `${baseUrl}/hierarchy/cost-centers?page=${page}&pageSize=${options.take}&query=${options.filter}`;

        sendRequest(url, dispatch, (data) => {
          options.setItems(
            data.items.map((x) => ({
              value: x.key,
              text: `${x.key} - ${x.description}`,
            })),
            data.total
          );
        });
      }
    });
    function sendRequest(url, dispatch, onloadSuccessCallback) {
      const xhr = new XMLHttpRequest();
      xhr.open("GET", url);
      xhr.setRequestHeader("Accept", "application/json");
      xhr.withCredentials = true;

      xhr.onload = () => {
        if (xhr.status === 200) {
          onloadSuccessCallback(JSON.parse(xhr.response));
        } else if (xhr.status === 401) {
          handleTokenRefresh(url, dispatch, onloadSuccessCallback);
        } else {
          console.error("XHR failed with status:", xhr.status);
        }
      };

      xhr.onerror = () => {
        console.error("XHR failed due to network error");
      };

      xhr.send();
    }

    function handleTokenRefresh(originalUrl, dispatch, callback) {
      const refreshXhr = new XMLHttpRequest();
      refreshXhr.open("POST", `${baseUrl}/auth/tokens:refresh`);
      refreshXhr.withCredentials = true;
      refreshXhr.setRequestHeader("Accept", "application/json");

      refreshXhr.onload = () => {
        if (refreshXhr.status === 200) {
          const result = JSON.parse(refreshXhr.response);
          if (result?.user) {
            dispatch(
              setCredentials({
                isAuthenticatedUser: true,
                user: result.user,
              })
            );
            sendRequest(originalUrl, dispatch, callback);
          } else {
            dispatch(logOut());
          }
        } else {
          dispatch(logOut());
        }
      };

      refreshXhr.send();
    }
    return <Survey model={survey} />;
  }
}

export default IncidentForm;
